import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "../components/usePageTitle";
import { gallery, pillars, stats, testimonials } from "../content/site";

/*
 * The home page, rebuilt to follow the 2020 WordPress home page (page 96) row by row:
 * hero, "discover", pillars, testimonials, gallery, counters, "enjoy a space",
 * "inspired by you", and the events call to action. The old page also carried an FAQ
 * and a closing photo band, but both were hidden on every screen size, so they are
 * not rebuilt here.
 */

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Becomes true once the element scrolls into view. */
function useInView<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reducedMotion() || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView] as const;
}

/** Text with a solid color block behind each line, like the old theme's "highlight" element. */
function Highlight({ lines, tone, id }: { lines: string[]; tone: "blue" | "burgundy"; id?: string }) {
  return (
    <h2 id={id} className={`highlight highlight-${tone}`}>
      {lines.map((line, i) => (
        <span key={line}>
          <mark>{line}</mark>
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </h2>
  );
}

function Button({ to, children }: { to: string; children: string }) {
  return (
    <Link className="p18-btn" to={to}>
      {children} <span aria-hidden="true" className="p18-btn-cursor">_</span>
    </Link>
  );
}

function Counter({ value, run }: { value: number; run: boolean }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!run) return;
    if (reducedMotion()) {
      setN(value);
      return;
    }
    const start = performance.now();
    const duration = 1800;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, value]);
  return <>{n}</>;
}

function Testimonials() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || reducedMotion()) return;
    const id = window.setInterval(() => setI((x) => (x + 1) % testimonials.length), 8000);
    return () => window.clearInterval(id);
  }, [paused]);

  return (
    <section
      className="h-testimonials"
      aria-roledescription="carousel"
      aria-label="What people are saying"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="h-grid h-testimonials-inner">
        <p className="h-t-label" aria-hidden="true">
          what people are saying
        </p>
        <svg className="h-t-quote" viewBox="0 0 93 74" aria-hidden="true">
          <path d="M0 74V44C0 18 12 4 36 0l4 10C26 14 20 24 20 36h18v38H0Zm53 0V44C53 18 65 4 89 0l4 10C79 14 73 24 73 36h18v38H53Z" />
        </svg>
        <div className="h-t-slides" aria-live={paused ? "polite" : "off"}>
          {testimonials.map((t, n) => (
            <figure
              key={t.name}
              className={n === i ? "h-t-slide is-active" : "h-t-slide"}
              aria-hidden={n !== i}
              role="group"
              aria-roledescription="slide"
              aria-label={`${n + 1} of ${testimonials.length}`}
            >
              <div className="h-t-content">
                <h3 className="h-t-title">{t.title}</h3>
                <blockquote className="h-t-text">
                  <img className="h-t-headshot" src={t.photo} alt="" width="100" height="100" loading="lazy" />
                  {t.quote}
                </blockquote>
                <figcaption className="h-t-author">
                  {t.name}
                  <span>{t.role}</span>
                </figcaption>
              </div>
              <img className="h-t-cutout" src={t.cutout} alt="" width="446" height="399" loading="lazy" />
            </figure>
          ))}
        </div>
        <div className="h-t-dots">
          {testimonials.map((t, n) => (
            <button
              key={t.name}
              type="button"
              className={n === i ? "is-active" : undefined}
              aria-label={`Show testimonial ${n + 1}: ${t.name}`}
              aria-current={n === i}
              onClick={() => setI(n)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryItem({ src, alt, side }: { src: string; alt: string; side: "left" | "right" }) {
  const [ref, inView] = useInView<HTMLLIElement>(0.2);
  return (
    <li ref={ref} className={`h-gallery-item from-${side}${inView ? " is-in" : ""}`}>
      <img src={src} alt={alt} width="771" height="551" loading="lazy" />
    </li>
  );
}

function Stats() {
  const [ref, inView] = useInView<HTMLElement>(0.3);
  return (
    <section ref={ref} className="h-stats" aria-label="Platform 18 by the numbers">
      <ul className="h-grid" role="list">
        {stats.map((s) => (
          <li key={s.label}>
            <img src={s.icon} alt="" height="65" />
            <span className="h-stat-value">
              <span aria-hidden="true">
                <Counter value={Number(s.value)} run={inView} />
              </span>
              <span className="sr-only">{s.value}</span>
            </span>
            <span className="h-stat-label">{s.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function Home() {
  usePageTitle();
  return (
    <div className="home">
      <section className="h-hero">
        <div className="h-hero-inner">
          <figure className="h-hero-photo">
            <img src="/images/hero-community.jpg" alt="A crowd of Platform 18 members smiling together" width="1345" height="664" />
            <img className="h-hero-badge" src="/images/home/badge.svg" alt="" aria-hidden="true" />
          </figure>
          <div className="h-hero-copy">
            <h1>
              find your place in the <br />
              detroit jewish community
            </h1>
            <p>Access it all (and more) at the hub @ Platform 18</p>
            <p className="h-hero-links">
              <Link to="/events">See all events</Link>
              <Link to="/contact">Get in touch</Link>
            </p>
          </div>
        </div>
      </section>

      <section className="h-discover" aria-labelledby="discover-heading">
        <picture className="h-discover-art">
          <source media="(max-width: 1023px)" srcSet="/images/building.jpg" />
          <img src="/images/home-building-cutout.png" alt="The Platform 18 building" width="1125" height="890" />
        </picture>
        <div className="h-discover-copy">
          <Highlight id="discover-heading" tone="blue" lines={["discover a", "game-changing", "community"]} />
          <p>
            Platform 18—a Partners Detroit Initiative—is where Jewish millennial professionals come to work or hang
            out, relax or re-energize
          </p>
        </div>
      </section>

      <section className="h-pillars" aria-label="What you’ll find">
        <div className="h-grid h-pillars-row">
          {pillars.map((p) => (
            <article key={p.title[1]} className="h-pillar">
              <img src={p.image} alt="" width="269" height="309" loading="lazy" />
              <h2>
                {p.title[0]}
                <br />
                <strong>{p.title[1]}</strong>
              </h2>
              <p>{p.body}</p>
              <p className="h-pillar-kicker">{p.kicker}</p>
            </article>
          ))}
        </div>
      </section>

      <Testimonials />

      <section className="h-gallery" aria-label="The space">
        <ul role="list">
          {gallery.map((g, n) => (
            <GalleryItem key={g.src} src={g.src} alt={g.alt} side={n % 2 === 0 ? "right" : "left"} />
          ))}
        </ul>
      </section>

      <Stats />

      <section className="h-space" aria-labelledby="space-heading">
        <div className="h-grid">
          <Highlight id="space-heading" tone="burgundy" lines={["enjoy a space that’s", "stimulating", "social and", "YOURS."]} />
        </div>
      </section>

      <section className="h-you" aria-labelledby="you-heading">
        <div className="h-you-inner">
          <div className="h-you-photo" role="img" aria-label="Two members talking over coffee" />
          <div className="h-you-copy">
            <p className="h-you-lead">The edgy decor, the artisanal food, the buzzing events —</p>
            <h2 id="you-heading">everything we do is created for and inspired by...you.</h2>
            <ul>
              <li>Your opinions.</li>
              <li>Your passions.</li>
              <li>Your downtime.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="h-events" aria-labelledby="events-heading">
        <div className="h-grid h-events-inner">
          <div className="h-events-copy">
            <h2 id="events-heading">the things you want to see, hear and get involved in</h2>
            <p>
              We make them happen —
              <br />
              in a community of Jewish millennials like you.
            </p>
            <div className="h-events-btns">
              <Button to="/events">See all events</Button>
              <Button to="/contact">Get in touch</Button>
            </div>
          </div>
          <div className="h-events-photo">
            <img src="/images/people-trip.jpg" alt="Platform 18 members on a group trip" width="839" height="506" loading="lazy" />
          </div>
        </div>
      </section>
    </div>
  );
}
